/**
 * RecommendationForm Component
 * User input form for mood, time available, streaming services, and country
 */

import React, { useState, useEffect } from 'react';

// Available streaming services
const SERVICES = [
    { id: 'netflix', name: 'Netflix', color: '#E50914' },
    { id: 'prime', name: 'Prime Video', color: '#00A8E1' },
    { id: 'disney+', name: 'Disney+', color: '#113CCF' },
    { id: 'hbo max', name: 'HBO Max', color: '#B100FF' },
    { id: 'hulu', name: 'Hulu', color: '#1CE783' },
    { id: 'apple tv+', name: 'Apple TV+', color: '#000000' }
];

// Time options in minutes
const TIME_OPTIONS = [
    { value: 15, label: '15 mins' },
    { value: 30, label: '30 mins' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
    { value: 999, label: '2+ hours' }
];

// Common moods/genres
const MOODS = [
    'Action', 'Adventure', 'Animation', 'Comedy', 'Crime',
    'Documentary', 'Drama', 'Family', 'Fantasy', 'Horror',
    'Mystery', 'Romance', 'Science Fiction', 'Thriller', 'Western'
];

// Countries with streaming services
const COUNTRIES = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'IN', name: 'India', flag: '🇮🇳' }
];

/**
 * Detect user's country based on timezone
 */
function detectCountry() {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Map common timezones to countries
    const timezoneMap = {
        'America/New_York': 'US',
        'America/Chicago': 'US',
        'America/Denver': 'US',
        'America/Los_Angeles': 'US',
        'America/Toronto': 'CA',
        'America/Vancouver': 'CA',
        'Europe/London': 'GB',
        'Europe/Berlin': 'DE',
        'Europe/Paris': 'FR',
        'Europe/Madrid': 'ES',
        'Europe/Rome': 'IT',
        'Australia/Sydney': 'AU',
        'Australia/Melbourne': 'AU',
        'Asia/Tokyo': 'JP',
        'America/Sao_Paulo': 'BR',
        'America/Mexico_City': 'MX',
        'Asia/Kolkata': 'IN'
    };

    return timezoneMap[timezone] || 'US'; // Default to US
}

function RecommendationForm({ onSubmit, onShare, loading, initialPreferences }) {
    const [selectedMoods, setSelectedMoods] = useState(initialPreferences?.moods || []);
    const [timeAvailable, setTimeAvailable] = useState(initialPreferences?.timeAvailable || 120);
    const [selectedServices, setSelectedServices] = useState(initialPreferences?.services || ['netflix']);
    const [country, setCountry] = useState(initialPreferences?.country || 'US');

    // Auto-detect country on mount
    useEffect(() => {
        const detectedCountry = detectCountry();
        if (!initialPreferences?.country) setCountry(detectedCountry);
    }, [initialPreferences]);

    useEffect(() => {
        try { localStorage.setItem('wtfnetflix-preferences', JSON.stringify({ moods: selectedMoods, timeAvailable, services: selectedServices, country })); } catch {}
    }, [selectedMoods, timeAvailable, selectedServices, country]);

    const preferences = () => ({ moods: selectedMoods, timeAvailable, services: selectedServices, country, count: 3 });

    /**
     * Toggle mood selection
     */
    const toggleMood = (mood) => {
        setSelectedMoods(prev =>
            prev.includes(mood)
                ? prev.filter(m => m !== mood)
                : [...prev, mood]
        );
    };

    /**
     * Toggle service selection
     */
    const toggleService = (serviceId) => {
        setSelectedServices(prev => {
            // Must have at least one service selected
            if (prev.includes(serviceId) && prev.length === 1) {
                return prev;
            }

            return prev.includes(serviceId)
                ? prev.filter(s => s !== serviceId)
                : [...prev, serviceId];
        });
    };

    /**
     * Handle form submission
     */
    const handleSubmit = (e) => {
        e.preventDefault();

        onSubmit(preferences());
    };

    return (
        <form className="recommendation-form" onSubmit={handleSubmit}>
            {/* Mood Selection */}
            <div className="form-section">
                <label className="form-label">
                    What's your mood? <span className="optional">(optional)</span>
                </label>
                <div className="mood-grid">
                    {MOODS.map(mood => (
                        <button
                            key={mood}
                            type="button"
                            className={`mood-tag ${selectedMoods.includes(mood) ? 'active' : ''}`}
                            onClick={() => toggleMood(mood)}
                        >
                            {mood}
                        </button>
                    ))}
                </div>
            </div>

            {/* Time Available */}
            <div className="form-section">
                <label className="form-label">
                    How much time do you have?
                </label>
                <div className="time-options">
                    {TIME_OPTIONS.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            className={`time-option ${timeAvailable === option.value ? 'active' : ''}`}
                            onClick={() => setTimeAvailable(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Streaming Services */}
            <div className="form-section">
                <label className="form-label">
                    Which streaming services do you have?
                </label>
                <div className="services-grid">
                    {SERVICES.map(service => (
                        <button
                            key={service.id}
                            type="button"
                            className={`service-tag ${selectedServices.includes(service.id) ? 'active' : ''}`}
                            onClick={() => toggleService(service.id)}
                            style={{
                                '--service-color': service.color
                            }}
                        >
                            {service.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Country Selection */}
            <div className="form-section">
                <label className="form-label" htmlFor="country-select">
                    Your Country
                </label>
                <select
                    id="country-select"
                    className="country-select"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                >
                    {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>
                            {c.flag} {c.name}
                        </option>
                    ))}
                </select>
                <p className="form-hint">
                    Auto-detected based on your timezone. Streaming availability varies by country.
                </p>
            </div>

            {/* Submit Button */}
            <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || selectedServices.length === 0}
            >
                {loading ? 'Finding...' : 'Get Recommendations'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => onShare(preferences())}>Share these filters</button>
        </form>
    );
}

export default RecommendationForm;
export { SERVICES, MOODS, COUNTRIES };
